import { chatLogin } from "../../../helpers/apis";
import { loginSuccess, logoutUserSuccess, apiError } from "./reducer";
import { setAuthorization } from "../../../helpers/api_helper";
import { jwtDecode } from "jwt-decode";

export const loginUser = (user, history) => async (dispatch) => {
  try {
    // Call login API
    // Backend response structure: { statusCode, status, message, data: { token, user } }
    const response = await chatLogin({
      email: user.email,
      password: user.password,
    });

    console.log("Login Response:", response); // Debug log
    console.log("Response data:", response?.data); // Debug log
    console.log("Response data keys:", response?.data ? Object.keys(response.data) : null); // Debug log

    // Handle different response structures
    let token = null;
    let userData = null;
    let responseStatus = null;

    // Check backend response structure: { statusCode, status, message, data: { token, user } }
    if (response && response.status === 1) {
      responseStatus = response.status;
      
      if (response.data) {
        // Log all keys in data to debug
        const dataKeys = Object.keys(response.data);
        console.log("Data keys:", dataKeys);
        console.log("Full data object:", JSON.stringify(response.data, null, 2));
        
        // Try multiple possible token field names
        token = response.data.token 
          || response.data.accessToken 
          || response.data.access_token
          || response.data.authToken
          || response.data.Token;
        
        // Try multiple possible user field names
        userData = response.data.user 
          || response.data.userData 
          || response.data.User
          || response.data.user_info;
        
        // If no separate user object, check if data itself contains user fields
        if (!userData && response.data) {
          const data = response.data;
          // If data has user-like fields (email, id, name, etc.) and no separate user field
          if ((data.email || data.id || data.name || data._id) && !data.user) {
            // Check if token is also in this data object
            if (data.token || data.accessToken) {
              userData = { ...data };
              // Remove token from userData if it exists
              delete userData.token;
              delete userData.accessToken;
              delete userData.access_token;
            } else {
              // Data might be the user object, but token might be missing
              userData = data;
            }
          }
        }
      } else if (response.token) {
        // Alternative: token and user at root level
        token = response.token;
        userData = response.user;
      }
      
      console.log("Extracted values:", { 
        hasToken: !!token, 
        tokenLength: token ? token.length : 0,
        hasUserData: !!userData,
        userDataKeys: userData ? Object.keys(userData) : null,
        responseStatus 
      });
    }

    // Check if we have valid response
    if (responseStatus === 1 && token) {
      // Check if user is admin
      if (!userData || !userData.isAdmin) {
        dispatch(apiError("Access denied. Admin access required."));
        return;
      }

      // Decode JWT to get expiry time
      let decodedToken;
      try {
        decodedToken = jwtDecode(token);
      } catch (decodeError) {
        console.error("Token decode error:", decodeError);
        decodedToken = { exp: Date.now() / 1000 + 86400 }; // Default 24 hours
      }

      // Store token and user data in localStorage
      const authData = {
        token: token,
        user: userData
      };
      
      localStorage.setItem("authUser", JSON.stringify(authData));
      localStorage.setItem("expiryAt", decodedToken.exp * 1000);
      localStorage.setItem("expiryIn", decodedToken.exp * 1000 - Date.now());

      console.log("Token stored in localStorage:", {
        hasToken: !!token,
        hasUser: !!userData,
        userIsAdmin: userData?.isAdmin
      });

      // Set Axios Authorization header
      setAuthorization(token);

      try {
        window.dispatchEvent(new CustomEvent("auth:token", { detail: { token } }));
      } catch (eventError) {
        console.warn("Unable to broadcast auth token event:", eventError);
      }

      // Dispatch success - clear any previous errors
      dispatch(loginSuccess({ token, user: userData }));

      // Redirect to dashboard immediately
      history("/dashboard");
    } else {
      // Handle case where status is 0 or response format is unexpected
      console.error("Login failed - Response details:", {
        response,
        hasStatus: !!response?.status,
        statusValue: response?.status,
        hasData: !!response?.data,
        dataKeys: response?.data ? Object.keys(response.data) : null,
        hasToken: !!response?.data?.token,
        hasUser: !!response?.data?.user
      });
      
      // If status is 1 but we're here, token extraction failed
      if (response?.status === 1) {
        dispatch(apiError("Login response format is incorrect. Token or user data is missing."));
      } else {
        const errorMessage = response?.message || "Invalid login response";
        dispatch(apiError(errorMessage));
      }
    }
  } catch (error) {
    console.error("Login Error:", error);
    dispatch(apiError(error.message || "Login failed. Please try again."));
  }
};

export const logoutUser = () => async (dispatch) => {
  try {
    localStorage.removeItem("authUser");
    localStorage.removeItem("expiryAt");
    localStorage.removeItem("expiryIn");
    try {
      window.dispatchEvent(new CustomEvent("auth:token", { detail: { token: null } }));
    } catch (eventError) {
      console.warn("Unable to broadcast logout token event:", eventError);
    }
    dispatch(logoutUserSuccess(true));
  } catch (error) {
    console.error("Logout Error:", error);
    dispatch(apiError(error.message || "An error occurred during logout."));
  }
};
